if (customElements && !customElements.get('additional-product-widget')) {
  class AdditionalProductWidget extends HTMLElement {
    constructor() {
      super();

      this.useDropdown = this.dataset.useDropdown === 'true';
      this.dropdownSelector = this.querySelector('.js-gift-product-dropdown');

      this.variables = {
        currentProductVariantHolder: document.querySelector('[id^=variant-selects-template-]') || null,
        availableGifts: this.querySelectorAll('.js-gift-product-selector input'),
        productForm: document.querySelector('form[action*="/cart/add"]'),
        dataAvailableGifts: []
      };
      
      // Store mapping of main product variant ID to gift product variant ID
      // Format: Map<mainVariantId, giftVariantId>
      this.giftProductMapping = new Map();
      
      // Store reverse mapping: which main products use each gift product
      // Format: Map<giftVariantId, Set<mainVariantId>>
      this.giftToMainProductsMapping = new Map();
      
      this.loadGiftProductMapping();
    }

    connectedCallback() {
      console.log('[AdditionalProductWidget] Connected, useDropdown:', this.useDropdown);

      if (this.useDropdown) {
        this.initDropdownMode();
      } else {
        if (!this.variables.currentProductVariantHolder) {
          console.warn('[AdditionalProductWidget] Product variant holder not found');
          return;
        }
        this.initDataAvailableGifts();
        this.setGift();
      }

      this.bindEvents();
      this.setupCartRemovalListener();
    }

    initDropdownMode() {
      if (!this.dropdownSelector) {
        console.warn('[AdditionalProductWidget] Dropdown selector not found');
        return;
      }

      // Set initial gift product ID from dropdown
      const selectedOption = this.dropdownSelector.selectedOptions[0];
      if (selectedOption) {
        this.currentGiftProductId = selectedOption.value;
        console.log('[AdditionalProductWidget] Dropdown mode initialized with product:', selectedOption.value);
        
        // Initialize image with first selected product
        let imageEl = this.querySelector('.gift-dropdown-product-image');
        if (!imageEl) {
          const imageContainer = this.querySelector('.gift-product-dropdown-image');
          imageEl = imageContainer ? imageContainer.querySelector('img') : null;
        }
        
        if (imageEl && selectedOption.dataset.productImage) {
          const initialImageSrc = selectedOption.dataset.productImage.trim();
          // Convert to absolute URL if relative
          let fullImageSrc = initialImageSrc;
          if (initialImageSrc.startsWith('http://') || initialImageSrc.startsWith('https://')) {
            // Already absolute URL, use as is
            fullImageSrc = initialImageSrc;
          } else if (initialImageSrc.startsWith('/') && !initialImageSrc.startsWith('//')) {
            // Relative path starting with /, add origin
            fullImageSrc = window.location.origin + initialImageSrc;
          } else if (initialImageSrc.startsWith('//')) {
            // Protocol-relative URL, add current protocol
            fullImageSrc = window.location.protocol + initialImageSrc;
          }
          // Otherwise use as is (might be data: URL or other format)
          
          // Remove srcset to avoid browser using cached responsive images
          imageEl.removeAttribute('srcset');
          imageEl.removeAttribute('sizes');
          
          imageEl.src = fullImageSrc;
          imageEl.alt = selectedOption.dataset.productTitle || '';
          imageEl.setAttribute('data-product-id', selectedOption.dataset.productId || '');
          imageEl.setAttribute('data-current-src', fullImageSrc);
          console.log('[AdditionalProductWidget] Initial image set to:', fullImageSrc, 'element:', imageEl);
        } else {
          console.warn('[AdditionalProductWidget] Could not initialize image', {
            imageEl: !!imageEl,
            hasImageData: !!selectedOption.dataset.productImage
          });
        }
      } else {
        console.warn('[AdditionalProductWidget] No selected option found in dropdown');
      }
    }

    get currentProductVariant() {
      const {currentProductVariantHolder} = this.variables;
      if (!currentProductVariantHolder) return null;

      return currentProductVariantHolder.querySelector('input:checked') || null;
    }

    get currentGiftProductId() {
      return this.dataset.currentGiftProductId || null;
    }

    set currentGiftProductId(value) {
      this.dataset.currentGiftProductId = value;
    }

    initDataAvailableGifts() {
      const {availableGifts, dataAvailableGifts} = this.variables;

      availableGifts.forEach((giftInput) => {
        const valueGiftVariantMinMax = giftInput.dataset.variantsName.split('-').map(item => Number(item));
        const giftProductId = giftInput.value;

        const availableSizes = [];

        for (let i = valueGiftVariantMinMax[0]; i <= valueGiftVariantMinMax[1]; i++) {
          availableSizes.push(i);
        }

        dataAvailableGifts.push({
          element: giftInput,
          availableSizes,
          giftProductId
        });
      });
    }

    async clearGiftsFromCart() {
      // Clear ALL gifts from cart - only one gift should be in cart at a time
      // The new gift will be added after clearing
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        if (!cartResponse.ok) return;
        
        const cart = await cartResponse.json();
        const productData = {};
        
        // Collect all gift variant IDs from dropdown options
        if (this.useDropdown && this.dropdownSelector) {
          const options = this.dropdownSelector.querySelectorAll('option');
          options.forEach((option) => {
            if (option.value) {
              const giftVariantId = option.value;
              // Check if this gift is in cart
              const giftInCart = cart.items.find(item => 
                item.variant_id?.toString() === giftVariantId
              );
              if (giftInCart) {
                productData[giftVariantId] = 0;
              }
            }
          });
        } else {
          // For old mode, use dataAvailableGifts
          const {dataAvailableGifts} = this.variables;
          dataAvailableGifts.forEach((gift) => {
            const giftVariantId = gift.giftProductId;
            const giftInCart = cart.items.find(item => 
              item.variant_id?.toString() === giftVariantId
            );
            if (giftInCart) {
              productData[giftVariantId] = 0;
            }
          });
        }

        // Update cart to remove all gifts
        if (Object.keys(productData).length > 0) {
          const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              updates: productData
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to clear gifts from cart');
          }
        }
      } catch (error) {
        console.error('Error clearing gifts from cart:', error);
      }
    }

    setGift() {
      const {dataAvailableGifts} = this.variables;
      const valueCurrentVariant = Number(this.currentProductVariant?.value) || 0;

      let dataGift = dataAvailableGifts.find(item => item.availableSizes.includes(valueCurrentVariant)) || dataAvailableGifts[0];

      dataGift.element.click();
      this.currentGiftProductId = dataGift.giftProductId;
    }

    handleSetGift() {
      const {currentProductVariantHolder} = this.variables;
      if (!currentProductVariantHolder) return;

      currentProductVariantHolder.addEventListener('change', (event) => {
        if (event.target.type === 'radio') {
          this.setGift();
        }
      });
    }

    updateCartDrawer() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    async addToCartGift(productId, quantity = 1) {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            updates: {
              [productId]: quantity
            }
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    }

    handleAddToCartGift() {
      const {productForm} = this.variables;
      if (!productForm) return;

      productForm.addEventListener('submit', async (event) => {
        if (!this.currentGiftProductId) return;

        // Get main product variant ID from form
        const mainProductVariantId = this.getMainProductVariantId();
        
        if (mainProductVariantId) {
          const mainVariantIdStr = mainProductVariantId.toString();
          const giftVariantIdStr = this.currentGiftProductId.toString();
          
          // Always use the gift from dropdown - it will replace any existing gift in cart
          // Remove old mapping if exists
          const existingGiftVariantId = this.giftProductMapping.get(mainVariantIdStr);
          if (existingGiftVariantId && existingGiftVariantId !== giftVariantIdStr) {
            const oldGiftSet = this.giftToMainProductsMapping.get(existingGiftVariantId);
            if (oldGiftSet) {
              oldGiftSet.delete(mainVariantIdStr);
              if (oldGiftSet.size === 0) {
                this.giftToMainProductsMapping.delete(existingGiftVariantId);
              }
            }
          }
          
          // Store mapping: main product variant -> gift product variant
          this.giftProductMapping.set(mainVariantIdStr, giftVariantIdStr);
          
          // Store reverse mapping: gift product -> set of main products
          if (!this.giftToMainProductsMapping.has(giftVariantIdStr)) {
            this.giftToMainProductsMapping.set(giftVariantIdStr, new Set());
          }
          this.giftToMainProductsMapping.get(giftVariantIdStr).add(mainVariantIdStr);
          
          this.saveGiftProductMapping();
          console.log('[AdditionalProductWidget] 💝 Stored gift mapping:', {
            mainVariant: mainProductVariantId,
            giftVariant: giftVariantIdStr,
            giftUsers: Array.from(this.giftToMainProductsMapping.get(giftVariantIdStr) || [])
          });
          
          // Use the gift to add
          this.currentGiftProductId = giftVariantIdStr;
          
          setTimeout(async () => {
            // Clear all existing gifts from cart first (only one gift should be in cart)
            await this.clearGiftsFromCart();
            
            // Then add the new gift from dropdown
            await this.addToCartGift(giftVariantIdStr);
            
            this.updateCartDrawer();
          }, 500);
        }
      });
    }

    getMainProductVariantId() {
      const {productForm} = this.variables;
      if (!productForm) return null;

      // Try to find variant ID in form
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        return variantInput.value;
      }

      // Try to find selected variant
      const variantSelect = productForm.querySelector('select[name="id"]');
      if (variantSelect) {
        return variantSelect.value;
      }

      // Try to find checked radio button
      const checkedVariant = productForm.querySelector('input[type="radio"][name*="id"]:checked');
      if (checkedVariant) {
        return checkedVariant.value;
      }

      return null;
    }

    loadGiftProductMapping() {
      try {
        const stored = localStorage.getItem('giftProductMapping');
        if (stored) {
          const mapping = JSON.parse(stored);
          this.giftProductMapping = new Map(mapping);
          
          // Rebuild reverse mapping
          this.giftToMainProductsMapping = new Map();
          this.giftProductMapping.forEach((giftVariantId, mainVariantId) => {
            if (!this.giftToMainProductsMapping.has(giftVariantId)) {
              this.giftToMainProductsMapping.set(giftVariantId, new Set());
            }
            this.giftToMainProductsMapping.get(giftVariantId).add(mainVariantId);
          });
          
          console.log('[AdditionalProductWidget] 📦 Loaded gift mapping from storage:', {
            mainToGift: Array.from(this.giftProductMapping.entries()),
            giftToMain: Array.from(this.giftToMainProductsMapping.entries()).map(([gift, mains]) => [gift, Array.from(mains)])
          });
        }
      } catch (error) {
        console.warn('[AdditionalProductWidget] Failed to load gift mapping:', error);
      }
    }

    saveGiftProductMapping() {
      try {
        const mapping = Array.from(this.giftProductMapping.entries());
        localStorage.setItem('giftProductMapping', JSON.stringify(mapping));
      } catch (error) {
        console.warn('[AdditionalProductWidget] Failed to save gift mapping:', error);
      }
    }

    async setupCartRemovalListener() {
      // Store previous cart state to detect removals
      let previousCartItems = [];
      
      const checkCartForRemovals = async () => {
        try {
          const response = await fetch(window.Shopify.routes.root + 'cart.js');
          if (!response.ok) return;
          
          const cart = await response.json();
          const currentVariantIds = new Set(
            (cart.items || []).map(item => item.variant_id?.toString()).filter(Boolean)
          );
          
          // Find removed items
          const removedVariantIds = previousCartItems.filter(
            variantId => !currentVariantIds.has(variantId)
          );
          
          if (removedVariantIds.length > 0) {
            console.log('[AdditionalProductWidget] 🔍 Detected removed items:', removedVariantIds);
            await this.handleCartItemRemoval(cart);
          }
          
          // Update previous state
          previousCartItems = Array.from(currentVariantIds);
        } catch (error) {
          console.warn('[AdditionalProductWidget] Failed to check cart:', error);
        }
      };
      
      // Initialize previous cart state
      await checkCartForRemovals();
      
      // Listen for cart changes - check after a delay to allow cart to update
      document.addEventListener('line-item:change:end', async (event) => {
        const {quantity} = event.detail || {};
        
        // If quantity is 0, item was removed - wait a bit then check cart
        if (quantity === 0) {
          setTimeout(async () => {
            await checkCartForRemovals();
          }, 500);
        }
      });

      // Also listen for cart refresh events
      document.addEventListener('cart:refresh', async () => {
        setTimeout(async () => {
          await checkCartForRemovals();
        }, 500);
      });
      
      // Periodic check as fallback (every 2 seconds)
      setInterval(async () => {
        await checkCartForRemovals();
      }, 2000);
    }

    async handleCartItemRemoval(cart) {
      if (!cart || !cart.items || this.giftProductMapping.size === 0) {
        return;
      }

      console.log('[AdditionalProductWidget] 🔍 Checking cart for gift products to remove', {
        cartItems: cart.items.map((item, index) => ({
          index: index + 1,
          variantId: item.variant_id,
          key: item.key,
          id: item.id,
          quantity: item.quantity
        })),
        mappings: Array.from(this.giftProductMapping.entries())
      });

      // Get all main product variant IDs that are still in cart
      const mainVariantIdsInCart = new Set();
      cart.items.forEach(item => {
        const variantId = item.variant_id?.toString();
        if (variantId && this.giftProductMapping.has(variantId)) {
          mainVariantIdsInCart.add(variantId);
        }
      });

      console.log('[AdditionalProductWidget] 📋 Main products still in cart:', Array.from(mainVariantIdsInCart));

      // Find gift products that should be removed
      // Gift should be removed only if there are NO main products in cart at all
      const giftVariantsToRemove = [];
      
      // Check if there are any main products in cart
      const hasAnyMainProductsInCart = mainVariantIdsInCart.size > 0;
      
      if (!hasAnyMainProductsInCart) {
        // No main products in cart - remove all gifts
        // Find all gift products in cart (from dropdown options)
        if (this.useDropdown && this.dropdownSelector) {
          const options = this.dropdownSelector.querySelectorAll('option');
          options.forEach((option) => {
            if (option.value) {
              const giftVariantId = option.value;
              const giftItem = cart.items.find((item, index) => {
                const itemVariantId = item.variant_id?.toString();
                return itemVariantId === giftVariantId;
              });
              
              if (giftItem) {
                const lineNumber = cart.items.indexOf(giftItem) + 1;
                giftVariantsToRemove.push({
                  line: lineNumber,
                  key: giftItem.key,
                  variantId: giftVariantId
                });
                console.log('[AdditionalProductWidget] 📌 Found gift to remove (no main products in cart):', {
                  giftVariantId,
                  line: lineNumber
                });
              }
            }
          });
        }
        
        // Clear all mappings
        this.giftProductMapping.clear();
        this.giftToMainProductsMapping.clear();
        this.saveGiftProductMapping();
      } else {
        // Some main products are still in cart - keep the gift
        // Just clean up mappings for deleted main products
        this.giftToMainProductsMapping.forEach((mainVariantIdsSet, giftVariantId) => {
          const removedMainVariantIds = Array.from(mainVariantIdsSet).filter(
            mainVariantId => !mainVariantIdsInCart.has(mainVariantId)
          );
          
          removedMainVariantIds.forEach(mainVariantId => {
            // Remove mapping for deleted main product
            this.giftProductMapping.delete(mainVariantId);
            mainVariantIdsSet.delete(mainVariantId);
            console.log('[AdditionalProductWidget] 🧹 Cleaned up mapping for removed main product:', {
              mainVariantId,
              giftVariantId,
              remainingMainProducts: Array.from(mainVariantIdsSet)
            });
          });
          
          // If no main products left in set, remove the set
          if (mainVariantIdsSet.size === 0) {
            this.giftToMainProductsMapping.delete(giftVariantId);
          }
        });
        
        // Clean up orphaned mappings
        const orphanedMainVariantIds = [];
        this.giftProductMapping.forEach((giftVariantId, mainVariantId) => {
          const giftSet = this.giftToMainProductsMapping.get(giftVariantId);
          if (!giftSet || !giftSet.has(mainVariantId)) {
            orphanedMainVariantIds.push(mainVariantId);
          }
        });
        orphanedMainVariantIds.forEach(mainVariantId => {
          this.giftProductMapping.delete(mainVariantId);
          console.log('[AdditionalProductWidget] 🧹 Removed orphaned mapping:', mainVariantId);
        });
      }

      // Remove gift products if their main products are gone
      if (giftVariantsToRemove.length > 0) {
        console.log('[AdditionalProductWidget] 🗑️ Removing gift products:', giftVariantsToRemove);
        
        // Remove gifts one by one
        for (const giftItem of giftVariantsToRemove) {
          try {
            console.log('[AdditionalProductWidget] 🚀 Attempting to remove gift:', {
              line: giftItem.line,
              variantId: giftItem.variantId
            });
            
            // Use line number for cart/change.js API
            const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                line: giftItem.line,
                quantity: 0
              })
            });

            if (response.ok) {
              const result = await response.json();
              console.log('[AdditionalProductWidget] ✅ Successfully removed gift product:', {
                variantId: giftItem.variantId,
                result: result
              });
              
              // Remove from reverse mapping (mappings already cleared above)
              this.giftToMainProductsMapping.delete(giftItem.variantId);
              
              this.saveGiftProductMapping();
            } else {
              const errorText = await response.text();
              let error;
              try {
                error = JSON.parse(errorText);
              } catch {
                error = { message: errorText };
              }
              console.warn('[AdditionalProductWidget] ⚠️ Failed to remove gift product:', {
                variantId: giftItem.variantId,
                line: giftItem.line,
                status: response.status,
                error: error
              });
            }
          } catch (error) {
            console.error('[AdditionalProductWidget] ❌ Error removing gift product:', error);
          }
          
          // Small delay between removals
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Refresh cart after removal
        setTimeout(() => {
          this.updateCartDrawer();
        }, 500);
      } else {
        console.log('[AdditionalProductWidget] ℹ️ No gift products to remove');
      }
    }

    handleDropdownChange() {
      if (!this.dropdownSelector) {
        console.warn('[AdditionalProductWidget] Dropdown selector not found for change handler');
        return;
      }

      this.dropdownSelector.addEventListener('change', async (event) => {
        const selectedOption = event.target.selectedOptions[0];
        if (!selectedOption) {
          console.warn('[AdditionalProductWidget] No selected option in change event');
          return;
        }

        // Update current gift product ID
        this.currentGiftProductId = selectedOption.value;
        const productId = selectedOption.dataset.productId;
        const productImage = selectedOption.dataset.productImage;
        const productTitle = selectedOption.dataset.productTitle;
        
        console.log('[AdditionalProductWidget] Product changed:', {
          variantId: selectedOption.value,
          productId: productId,
          productImage: productImage,
          productTitle: productTitle
        });

        // Update image - try multiple selectors to find the image
        let imageEl = this.querySelector('.gift-dropdown-product-image');
        if (!imageEl) {
          // Try alternative selector
          const imageContainer = this.querySelector('.gift-product-dropdown-image');
          imageEl = imageContainer ? imageContainer.querySelector('img') : null;
        }
        
        // Debug: log all available data
        console.log('[AdditionalProductWidget] 🔍 Change event data:', {
          variantId: selectedOption.value,
          productId: productId,
          productImage: productImage,
          productTitle: productTitle,
          imageElFound: !!imageEl,
          currentImageSrc: imageEl ? imageEl.src : 'no element',
          allOptions: Array.from(event.target.options).map(opt => ({
            value: opt.value,
            text: opt.text,
            productId: opt.dataset.productId,
            productImage: opt.dataset.productImage,
            selected: opt.selected
          }))
        });
        
        if (!imageEl) {
          console.error('[AdditionalProductWidget] ❌ Image element not found!', {
            allImages: Array.from(this.querySelectorAll('img')).map(img => ({
              src: img.src,
              class: img.className,
              alt: img.alt,
              parent: img.parentElement ? img.parentElement.className : 'no parent'
            }))
          });
          return;
        }
        
        const newImageSrc = productImage ? productImage.trim() : null;
        
        if (newImageSrc) {
          // Method 1: Direct update with cache busting
          console.log('[AdditionalProductWidget] 🖼️ Updating image directly:', newImageSrc);
          this.updateImageElement(imageEl, newImageSrc, productTitle, productId);
        } else if (productId) {
          // Method 2: Try to get image via REST API if productId is available
          console.log('[AdditionalProductWidget] 🔄 No image URL, fetching via API for product:', productId);
          this.fetchProductImage(productId).then(imageUrl => {
            if (imageUrl) {
              console.log('[AdditionalProductWidget] ✅ Got image from API:', imageUrl);
              this.updateImageElement(imageEl, imageUrl, productTitle, productId);
            } else {
              console.error('[AdditionalProductWidget] ❌ Failed to get image from API');
            }
          }).catch(err => {
            console.warn('[AdditionalProductWidget] ⚠️ Failed to fetch image via API:', err);
          });
        } else {
          console.error('[AdditionalProductWidget] ❌ No image source available - no productImage and no productId');
        }

        // Update title (keep "Dr. Worker beanie" static)
        // Title stays the same as per design requirements

        // Update price
        const priceEl = this.querySelector('.js-gift-product-price');
        if (priceEl && selectedOption.dataset.productPrice) {
          const price = parseInt(selectedOption.dataset.productPrice);
          // Format price to match Shopify money format (price is in cents)
          // Convert to dollars and format without trailing zeros
          const priceInDollars = price / 100;
          let formattedPrice = priceInDollars.toFixed(2);
          // Remove trailing zeros
          formattedPrice = formattedPrice.replace(/\.?0+$/, '');
          // Use simple format matching the Liquid template format
          priceEl.textContent = '$' + formattedPrice;
        } else if (priceEl && !selectedOption.dataset.productPrice) {
          console.warn('[AdditionalProductWidget] No product price data found');
        }
      });
    }

    updateImageElement(imageEl, imageSrc, altText, productId) {
      if (!imageEl || !imageSrc) {
        console.error('[AdditionalProductWidget] Cannot update image - missing element or src');
        return;
      }
      
      const newImageSrc = imageSrc.trim();
      
      // Convert relative URL to absolute if needed
      // Check if URL is already absolute (starts with http:// or https://)
      let fullImageSrc = newImageSrc;
      if (newImageSrc.startsWith('http://') || newImageSrc.startsWith('https://')) {
        // Already absolute URL, use as is
        fullImageSrc = newImageSrc;
      } else if (newImageSrc.startsWith('/') && !newImageSrc.startsWith('//')) {
        // Relative path starting with /, add origin
        fullImageSrc = window.location.origin + newImageSrc;
      } else if (newImageSrc.startsWith('//')) {
        // Protocol-relative URL, add current protocol
        fullImageSrc = window.location.protocol + newImageSrc;
      }
      // Otherwise use as is (might be data: URL or other format)
      
      // Add cache busting parameter to force reload
      const separator = fullImageSrc.includes('?') ? '&' : '?';
      const timestamp = Date.now();
      const cacheBustedSrc = `${fullImageSrc}${separator}_t=${timestamp}`;
      
      const currentSrc = imageEl.src || imageEl.getAttribute('src') || '';
      console.log('[AdditionalProductWidget] 🖼️ Updating image:', {
        original: newImageSrc,
        full: fullImageSrc,
        cacheBusted: cacheBustedSrc,
        currentSrc: currentSrc,
        willChange: currentSrc !== fullImageSrc
      });
      
      // Step 1: CRITICAL - Remove srcset and sizes FIRST (browser uses these instead of src!)
      imageEl.removeAttribute('srcset');
      imageEl.removeAttribute('sizes');
      
      // Step 2: Clear current src completely
      const oldSrc = imageEl.src;
      imageEl.removeAttribute('src');
      imageEl.src = '';
      
      // Step 3: Force browser to forget the old image by hiding it
      const wasVisible = imageEl.style.display !== 'none';
      if (wasVisible) {
        imageEl.style.display = 'none';
      }
      
      // Step 4: Preload new image with cache busting
      const tempImg = new Image();
      tempImg.crossOrigin = 'anonymous'; // Help with CORS if needed
      
      tempImg.onload = () => {
        console.log('[AdditionalProductWidget] 📥 Image preloaded successfully');
        
        // Step 5: Update the actual image element with absolute URL
        // Use cache-busted URL first, then switch to clean URL
        imageEl.src = cacheBustedSrc;
        
        // Force immediate update
        requestAnimationFrame(() => {
          imageEl.src = fullImageSrc;
          imageEl.alt = altText || '';
          imageEl.setAttribute('data-product-id', productId || '');
          imageEl.setAttribute('data-current-src', fullImageSrc);
          
          // Ensure srcset and sizes are removed
          imageEl.removeAttribute('srcset');
          imageEl.removeAttribute('sizes');
          
          // Show image with fade effect
          if (wasVisible) {
            imageEl.style.display = '';
            imageEl.style.opacity = '0';
            requestAnimationFrame(() => {
              imageEl.style.opacity = '1';
              imageEl.style.transition = 'opacity 0.15s ease-in';
            });
          }
          
          console.log('[AdditionalProductWidget] ✅ Image successfully updated:', fullImageSrc);
        });
      };
      
      tempImg.onerror = (error) => {
        console.warn('[AdditionalProductWidget] ⚠️ Preload failed, updating directly:', error);
        
        // Fallback: update directly even if preload fails
        imageEl.src = fullImageSrc;
        imageEl.alt = altText || '';
        imageEl.setAttribute('data-product-id', productId || '');
        imageEl.setAttribute('data-current-src', fullImageSrc);
        imageEl.removeAttribute('srcset');
        imageEl.removeAttribute('sizes');
        
        if (wasVisible) {
          imageEl.style.display = '';
        }
      };
      
      // Start preloading
      tempImg.src = cacheBustedSrc;
    }

    async fetchProductImage(productId) {
      try {
        // Try to fetch product data via Shopify REST API
        const response = await fetch(`/products/${productId}.js`);
        if (response.ok) {
          const product = await response.json();
          if (product.images && product.images.length > 0) {
            return product.images[0];
          }
        }
      } catch (error) {
        console.warn('[AdditionalProductWidget] REST API fetch failed:', error);
      }
      return null;
    }

    bindEvents() {
      if (this.useDropdown) {
        this.handleDropdownChange();
      } else {
        this.handleSetGift();
      }
      this.handleAddToCartGift();
    }
  }

  customElements.define('additional-product-widget', AdditionalProductWidget);
}